import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "api";
import { useFetchStateHelper } from "hooks/useFetchState";

export const useCharts = (fieldId) => {
  const { data, ...queryResponse } = useQuery({
    queryKey: ["charts", fieldId],
    queryFn: () => api.charts.fetchAll(fieldId),
    enabled: !!fieldId,
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return {
    data,
    fetchState,
  };
};

export const useChart = (chartId, fieldId) => {
  const { data, ...queryResponse } = useQuery({
    queryKey: ["chart", chartId, fieldId],
    queryFn: () => api.charts.fetchOne(chartId),
    enabled: !!chartId && !!fieldId,
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return {
    data,
    fetchState,
  };
};

export const useChartsConfig = () => {
  const { data, ...queryResponse } = useQuery({
    queryKey: ["charts-config"],
    queryFn: api.charts.fetchConfig,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return {
    data,
    fetchState,
  };
};

export const useChartsUpdate = (chartId, fieldId) => {
  const qc = useQueryClient();
  const { mutateAsync, data, ...mutateResponse } = useMutation({
    mutationKey: ["update-chart", fieldId, chartId],
    mutationFn: (payload) => api.charts.update(chartId, payload),
    onSuccess: (newChart) => {
      qc.setQueryData(["charts", fieldId], (old) => {
        return (old ?? []).map((chart) =>
          chart.id === chartId ? newChart : chart
        );
      });
    },
  });
  const { fetchState } = useFetchStateHelper(mutateResponse);

  return {
    data,
    updateChart: mutateAsync,
    fetchState,
  };
};

export const useChartsCreate = (fieldId) => {
  const qc = useQueryClient();
  const { mutateAsync, data, ...mutateResponse } = useMutation({
    mutationKey: ["create-chart", fieldId],
    mutationFn: (chartData) => api.charts.create(chartData),
    onSuccess: (newChart) => {
      qc.setQueryData(["charts", fieldId], (old) => {
        return [...(old ?? []), newChart];
      });
    },
  });
  const { fetchState } = useFetchStateHelper(mutateResponse);

  return {
    data,
    createChart: mutateAsync,
    fetchState,
  };
};

export const useChartsDelete = (chartId, fieldId) => {
  const qc = useQueryClient();
  const { mutateAsync, data, ...mutateResponse } = useMutation({
    mutationKey: ["delete-chart", fieldId, chartId],
    mutationFn: () => api.charts.delete(chartId),
    onSuccess: () => {
      qc.setQueryData(["charts", fieldId], (old) => {
        return (old ?? []).filter((chart) => chart.id !== chartId);
      });
    },
  });
  const { fetchState } = useFetchStateHelper(mutateResponse);

  return {
    data,
    deleteChart: mutateAsync,
    fetchState,
  };
};

export const useChartsReorder = (fieldId) => {
  const qc = useQueryClient();

  const { mutateAsync, data, ...mutateResponse } = useMutation({
    mutationKey: ["delete-chart", fieldId, "reorder"],
    mutationFn: (payload) => api.charts.reorder(fieldId, payload),
    // Optimistic update
    onMutate: async (payload) => {
      // Cancel outgoing refetches
      await qc.cancelQueries({ queryKey: ["charts", fieldId] });
      // Snapshot the previous data
      const previousCharts = qc.getQueryData(["charts", fieldId]);
      // Optimistically update orders
      qc.setQueryData(["charts", fieldId], (old) => {
        const updated = (old ?? []).map((chart) => {
          if (payload[chart.id] !== undefined) {
            return { ...chart, order: payload[chart.id] };
          }
          return chart;
        });
        // Sort by 'order' property ascending
        return updated.sort((a, b) => a.order - b.order);
      });
      // Return snapshot for rollback
      return { previousCharts };
    },
    // If failed → rollback
    onError: (_error, _newChart, ctx) => {
      qc.setQueryData(["charts", fieldId], ctx.previousCharts);
    },
    // Finally refetch if needed
    onSettled: () => {
      // qc.invalidateQueries({ queryKey: ["charts", fieldId] });
    },
  });
  const { fetchState } = useFetchStateHelper(mutateResponse);
  return {
    data,
    reorderCharts: mutateAsync,
    fetchState,
  };
};
